import { useState, useEffect, useRef } from "react";
import markdownStyles from "./markdown-styles.module.css";
import { BookSession } from "./book-session";

export default function PostBody({ content }) {
  const [headings, setHeadings] = useState([]);
  const [activeHeading, setActiveHeading] = useState(null);
  const [isSticky, setIsSticky] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isTOCVisible, setIsTOCVisible] = useState(true);
  const [isLargeScreen, setIsLargeScreen] = useState(true);
  const [isBookSessionVisible, setIsBookSessionVisible] = useState(true); // NEW

  const contentRef = useRef(null);
  const bookSessionRef = useRef(null); // NEW

  useEffect(() => {
    let headingElements = [];
    let match;
    let regexH2 = /<(h2) id="([^"]+)">([\s\S]*?)<\/h2>/g;
    let regexH3 = /<(h3) id="([^"]+)">([\s\S]*?)<\/h3>/g;

    while ((match = regexH2.exec(content)) !== null) {
      const rawText = match[3]
        .replace(/<[^>]+>/g, "")
        .replace(/&nbsp;|&#160;/gi, " ")
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .trim();

      if (rawText) {
        headingElements.push({ level: match[1], id: match[2], text: rawText });
      }
    }

    if (headingElements.length === 0) {
      while ((match = regexH3.exec(content)) !== null) {
        const rawText = match[3]
          .replace(/<[^>]+>/g, "")
          .replace(/&nbsp;|&#160;/gi, " ")
          .replace(/&amp;/g, "&")
          .replace(/&lt;/g, "<")
          .replace(/&gt;/g, ">")
          .replace(/&quot;/g, '"')
          .replace(/&#39;/g, "'")
          .trim();

        if (rawText) {
          headingElements.push({
            level: match[1],
            id: match[2],
            text: rawText,
          });
        }
      }
    }

    setHeadings(headingElements);

    const updateScreenSize = () => {
      const width = window.innerWidth;
      setIsLargeScreen(width > 1279);
      setIsFullScreen(width <= 1279 && width >= 1025);
    };
    updateScreenSize();
    window.addEventListener("resize", updateScreenSize);
    return () => window.removeEventListener("resize", updateScreenSize);
  }, [content]);

  useEffect(() => {
    if (!content) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (visible.length > 0) {
          setActiveHeading(visible[0].target.id);
        }
      },
      {
        root: null,
        rootMargin: "0px 0px -70% 0px",
        threshold: 0.25,
      }
    );

    const timeout = setTimeout(() => {
      const headingEls = Array.from(document.querySelectorAll("h1, h2, h3"));
      headingEls.forEach((el) => observer.observe(el));
    }, 300);

    return () => {
      clearTimeout(timeout);
      observer.disconnect();
    };
  }, [content]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setIsSticky(currentScrollY > 250);
      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!contentRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsTOCVisible(entry.isIntersecting),
      { root: null, threshold: 0 }
    );
    observer.observe(contentRef.current);
    return () => observer.disconnect();
  }, []);

  // BookSession visibility observer (same as TOC)
  useEffect(() => {
    if (!contentRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsBookSessionVisible(entry.isIntersecting),
      { root: null, threshold: 0 }
    );
    observer.observe(contentRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="flex flex-col md:max-w-full   relative">
      {/* TOC - Left Sidebar */}
      {isTOCVisible && (
        <div
          className={`${
            isSticky ? "fixed" : "absolute"
          } top-24 hidden xl:block`}
          style={{
            left: 0,
            width: "20%",
            height: "fit-content",
            maxHeight: "100vh",
            overflowY: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          <style>{`div::-webkit-scrollbar { display: none; }`}</style>
          <div className="bg-white p-8">
            <h3 className="text-lg font-bold text-[#ff9245] uppercase mb-4">
              Table of Contents
            </h3>
            <ul className="space-y-2">
              {headings.map((heading) => (
                <li key={heading.id} className="ml-2">
                  <a
                    onClick={(e) => {
                      e.preventDefault();
                      const target = document.getElementById(heading.id);
                      if (target) {
                        const yOffset = -100;
                        const y =
                          target.getBoundingClientRect().top +
                          window.pageYOffset +
                          yOffset;
                        window.scrollTo({ top: y, behavior: "smooth" });
                        setActiveHeading(heading.id);
                      }
                    }}
                    className={`text-sm cursor-pointer transition block ${
                      activeHeading === heading.id
                        ? "font-semibold text-[#FF914D] underline decoration-[#FF9245]"
                        : "font-normal text-[#444]"
                    }`}
                  >
                    {heading.text}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* BookSession - Right Sidebar */}
      {isLargeScreen && isBookSessionVisible && (
        <div
          className={`${
            isSticky ? "fixed" : "absolute"
          } top-24 hidden xl:block`}
          style={{
            right: 0,
            width: "20%",
            height: "fit-content",
            maxHeight: "80vh", // Set a maxHeight for scrollability
            overflowY: "auto", // Make scrollable
          }}
        >
          <div className="p-4 rounded-lg">
            <BookSession />
          </div>
        </div>
      )}

      {/* TOC for smaller screens */}
      <div className="block xl:hidden w-full mt-6 mb-8 px-[5%]">
        <div className="bg-[#f8f9fb] rounded-lg border border-gray-200 p-4 shadow-md">
          <h3 className="text-base font-bold text-[#ff9245] uppercase mb-4">
            Table of Contents
          </h3>
          <ul className="space-y-2">
            {headings.map((heading) => (
              <li key={heading.id}>
                <a
                  onClick={(e) => {
                    e.preventDefault();
                    const target = document.getElementById(heading.id);
                    if (target) {
                      const yOffset = -100;
                      const y =
                        target.getBoundingClientRect().top +
                        window.pageYOffset +
                        yOffset;
                      window.scrollTo({ top: y, behavior: "smooth" });
                      setActiveHeading(heading.id);
                    }
                  }}
                  className="text-sm cursor-pointer hover:text-[#FF914D] transition block"
                  style={{
                    fontWeight: activeHeading === heading.id ? "600" : "400",
                    color: activeHeading === heading.id ? "#FF914D" : "#444",
                    textDecoration:
                      activeHeading === heading.id ? "underline" : "none",
                    textDecorationColor: "#FF9245",
                  }}
                >
                  {heading.text}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* BookSession below TOC for screens < 1024px */}
      <div className="block xl:hidden w-full mb-8 px-[5%]">
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-md">
          <BookSession />
        </div>
      </div>

      {/* Main Content in Center */}
      <div
        ref={contentRef}
        className={`${markdownStyles["markdown"]} w-full xl:!mt-[104px] px-4 sm:px-6 md:px-8 xl:w-[60%] xl:px-10`}
        style={{
          marginLeft: isLargeScreen ? "auto" : "20px", // Add margin-left on small screens
          marginRight: isLargeScreen ? "auto" : "20px", // Add margin-right on small screens
          maxWidth: isLargeScreen ? "100%" : "calc(100% - 40px)", // Adjust maxWidth for smaller screens to prevent overlap
        }}
        dangerouslySetInnerHTML={{ __html: content }}
      />

      {/* Invisible marker for BookSession visibility - move inside main content wrapper */}
      <div ref={bookSessionRef} style={{ height: 1 }} />
    </div>
  );
}
